import React, { useState } from 'react';
import ExperienceType from '../../constants/ExperienceType';
import { useExperienceBooking } from '../../hooks';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { formatDate } from '@koob/dates';
import { decodeId, formatCurrency } from '../../utils';
import { Stack } from '@koob/margaret';
import KoobTitle from '../Koob/KoobTitle';
import {
  KoobBadge,
  KoobButton,
  KoobLoader,
  KoobModal,
  KoobSlideOver,
  sanitizeRichText,
} from '@koob/ui';
import { useNavigate } from 'react-router-dom';
import { BookingState } from '../Hotel/Booking/BookingRecap';
import { useAsync } from 'react-async';
import { getExperience } from '../../api/experiences';
import ExperienceExtraComposition from '../../containers/Experiences/Partials/ExperienceExtraComposition';
import ExperienceExtrasModal from '../../containers/Experiences/Partials/ExperienceExtrasModal';
import { useLanguage } from '../../hooks/useContexts';
import { useTripStore, useDays } from '@koob/trip-designer';
import { KoobAccordion } from '@koob/ui';
import BookingCountdown from '../Hotel/Booking/BookingCountdown';
import {
  getExtraOccurrenceIdentityKey,
  getExtraProductKey,
  assignTravelersToRooms,
  getExtraTitle,
  getResolvedExtrasPrices,
  getRoomPax,
  getSecondaryExtras,
  isProgramIncludedExtra,
  sortExtras,
} from '@koob/utils';
import ExperiencesBasketState from './ExperiencesBasketState';
import ExperienceGuaranteedDepartureIcon from './ExperienceGuaranteedDepartureIcon';
import HotelRoomSelectionModal from '../Hotel/HotelRoomSelectionModal';
import { getRoomsAvailability } from '../../api/hotels';
import { findRoomByKey, findRoomsByKeys } from '../../utils/roomKeyGenerator';
import { useFormikContext } from 'formik';
import {
  ExperiencePricingTypeEnum,
  ExtraScopeEnum,
  ExtraSourceEnum,
  ProgramAccommodationTypeEnum,
} from '@koob/enums';
import { ExperienceDynamicCancelConditions } from '@koob/booking';

// Re-wrap normalized { requestId, expiresAt, possibilities } as { data: envelope }.
const getHotelDmcRoom = async ({ id, filters, locale }) => ({
  data: await getRoomsAvailability({ id, filters, locale }),
});

const LigneHorizontal = styled.hr`
  border: 0.00001em solid #d1d5db;
  width: 99%;
  opacity: 0.4;
  margin-top: 5px;
`;

const Promotion = styled.div`
  background-color: rgba(115, 201, 146, 0.12);
  color: ${({ theme }) => theme.primary} !important;
  white-space: nowrap;
  font-size: 15px;
  font-weight: 700;
  border-radius: 10px;
  padding: ${({ theme }) => theme.spacing(0.295)}
    ${({ theme }) => theme.spacing(0.6)};
  text-align: center;
`;

const ModalCondition = styled.span`
  font-weight: 800;
  text-transform: lowercase;
  color: #1b8ce9;
  text-decoration: underline;
  text-decoration-color: #1b8ce9;
  cursor: pointer;
`;

function ExperienceBasketProgramContentDay({
  index,
  program,
  selectedHotel,
  tripDay,
  hotelsMap,
}) {
  const { t } = useTranslation('experiences');

  // Support both new normalized structure and old structure for backward compatibility
  const tripDayHotel =
    hotelsMap && tripDay?.hotelId
      ? hotelsMap[tripDay.hotelId] // New structure: look up hotel in map
      : tripDay?.hotel; // Old structure: use hotel object directly

  const shouldShowBookedOnMyOwn =
    selectedHotel?.bookedOnMyOwn || tripDay?.hotelBookedOnMyOwn;

  const hotelName =
    tripDayHotel?.displayName ??
    selectedHotel?.displayName ??
    program?.accomodationHotel?.displayName ??
    program?.accomodationExperience?.name;

  return (
    <>
      <div className="font-semibold leading-none">
        {`${t('basket.day')} ${index + 1} - ${program?.title}`}
      </div>

      {program?.accomodationType !==
      ProgramAccommodationTypeEnum.NO_ACCOMMODATION ? (
        <>
          <div className="flex items-center space-x-1.5">
            {[
              ProgramAccommodationTypeEnum.HOTEL,
              ProgramAccommodationTypeEnum.FREE_ACCOMMODATION,
            ].includes(program?.accomodationType) && (
              <svg className="h-4 w-4" viewBox="0 0 640 512">
                <path d="M32 48c0-8.8-7.2-16-16-16S0 39.2 0 48V336v64 64c0 8.8 7.2 16 16 16s16-7.2 16-16V416H608v48c0 8.8 7.2 16 16 16s16-7.2 16-16V400 336 240c0-61.9-50.1-112-112-112H304c-26.5 0-48 21.5-48 48V320H32V48zM608 384H32V352H272 608v32zm0-144v80H288V176c0-8.8 7.2-16 16-16H528c44.2 0 80 35.8 80 80zM96 208a48 48 0 1 1 96 0 48 48 0 1 1 -96 0zm128 0A80 80 0 1 0 64 208a80 80 0 1 0 160 0z" />
              </svg>
            )}

            {program?.accomodationType === 'nightTransfer' && (
              <svg className="h-4 w-4" viewBox="0 0 384 512">
                <path d="M352 192c0-88.4-71.6-160-160-160S32 103.6 32 192c0 20.2 9.1 48.6 26.5 82.7c16.9 33.2 39.9 68.2 63.4 100.5c23.4 32.2 46.9 61 64.5 81.9c1.9 2.3 3.8 4.5 5.6 6.6c1.8-2.1 3.6-4.3 5.6-6.6c17.7-20.8 41.1-49.7 64.5-81.9c23.5-32.3 46.4-67.3 63.4-100.5C342.9 240.6 352 212.2 352 192zm32 0c0 88.8-120.7 237.9-170.7 295.9C200.2 503.1 192 512 192 512s-8.2-8.9-21.3-24.1C120.7 429.9 0 280.8 0 192C0 86 86 0 192 0S384 86 384 192zm-240 0a48 48 0 1 0 96 0 48 48 0 1 0 -96 0zm48 80a80 80 0 1 1 0-160 80 80 0 1 1 0 160z" />
              </svg>
            )}

            <div>
              {shouldShowBookedOnMyOwn ? (
                <div className="font-medium text-orange-500">
                  {t('accomodationHotel.bookedOnMyOwn')}
                  <i className="fa fa-arrow-up-right-from-square ml-1.5" />
                </div>
              ) : (
                hotelName
              )}
            </div>

            <div className="text-sm font-medium">
              {program?.isIncludedAccomodation && (
                <div className="text-green-500">
                  {t('accomodationHotel.included')}
                </div>
              )}
            </div>
          </div>
        </>
      ) : (
        <div className="flex items-center space-x-1.5">
          <svg className="h-4 w-4" viewBox="0 0 384 512">
            <path d="M324.5 411.1c6.2 6.2 16.4 6.2 22.6 0s6.2-16.4 0-22.6L214.6 256 347.1 123.5c6.2-6.2 6.2-16.4 0-22.6s-16.4-6.2-22.6 0L192 233.4 59.5 100.9c-6.2-6.2-16.4-6.2-22.6 0s-6.2 16.4 0 22.6L169.4 256 36.9 388.5c-6.2 6.2-6.2 16.4 0 22.6s16.4 6.2 22.6 0L192 278.6 324.5 411.1z" />
          </svg>

          <div className="text-sm font-medium">
            {t('detail.programContent.noAccomodation')}
          </div>
        </div>
      )}
    </>
  );
}

function ExperienceBasketProgramContent({
  experienceId,
  selectedHotels,
  tripDays,
  experiencesMap,
  hotelsMap,
}) {
  const { lang } = useLanguage();
  const { data, isLoading } = useAsync({
    promiseFn: getExperience,
    experienceId,
    locale: lang,
  });
  const programs = data?.data?.programs;

  // Support both new normalized structure and old structure for backward compatibility
  const programDays = tripDays?.filter(
    item =>
      // New structure: check experienceId
      (experiencesMap && item.experienceId === experienceId) ||
      // Old structure: check experience.id
      (!experiencesMap && item.experience?.id === experienceId),
  );

  return !isLoading ? (
    <div className="bg-gray-100 max-h-56 overflow-y-scroll rounded-lg p-4">
      <div className="text-gray-500 flex flex-col space-y-3">
        {programs?.map((program, index) => (
          <ExperienceBasketProgramContentDay
            key={program?.id}
            index={index}
            program={program}
            tripDay={programDays?.[index]}
            selectedHotel={selectedHotels?.find(
              h => h.programId === program?.id,
            )}
            hotelsMap={hotelsMap}
          />
        ))}
      </div>
    </div>
  ) : null;
}

export const ExperienceBasketCard = ({
  experience,
  enableRemoval = true,
  isCard = true,
  showPrice = true,
  setNewRoom,
  isHotel,
  expirationDate,
  setExpirationDate,
  refreshBasket,
  bookingDone,
  booking,
  isCheckout = false, // Only auto-select rooms when on checkout page
  locale,
}) => {
  const { t } = useTranslation('experiences', { keyPrefix: 'basket' });
  const { removeExperienceFromBasket, setBasket, basket } =
    useExperienceBooking();
  const navigate = useNavigate();

  // Try to get Formik context for checkout form (not trip designer state)
  // This is used to sync traveler info between basket and checkout form
  let formik;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    formik = useFormikContext();
  } catch {
    // Not in Formik context (e.g., in basket popover), that's fine
    formik = null;
  }

  // Get data and actions from Zustand store (for trip designer state)
  const days = useDays();
  const experiences = useTripStore(state => state.experiences);
  const hotels = useTripStore(state => state.hotels);
  const updateDays = useTripStore(state => state.updateDays);

  const [showRoomSelectModal, setShowRoomSelectModal] = useState(false);
  // Use experience.internalId to make hasAutoSelected unique per basket item
  const [hasAutoSelected, setHasAutoSelected] = React.useState(false);

  // Only process hotel-specific logic for hotel types
  const isHotelType = experience?.type === 'hotel';

  // Normalize experienceComposition to always be an array
  // For hotels, composition can be in experience.filters.experienceComposition or experience.experienceComposition
  const roomsComposition = (() => {
    if (!isHotelType) return [];

    // Check experience.experienceComposition first (set when added from ExperienceDetails)
    const composition =
      experience?.experienceComposition ||
      experience?.filters?.experienceComposition;

    if (!composition) return [];

    return Array.isArray(composition) ? composition : [composition];
  })();

  // Decode hotel ID once (only for hotels)
  const decodedHotelId =
    isHotelType && experience?.hotelId ? decodeId(experience.hotelId) : null;

  // Fetch rooms available for hotels (to auto-select if room key exists)
  // Only fetch when on checkout page to avoid unnecessary API calls in basket overlay
  const shouldFetchRooms =
    isCheckout &&
    isHotelType &&
    decodedHotelId &&
    roomsComposition.length > 0 &&
    (!experience.selectedRooms || experience.selectedRooms.length === 0);

  const skipFetch =
    !isHotelType ||
    !decodedHotelId ||
    roomsComposition.length === 0 ||
    (!shouldFetchRooms && !showRoomSelectModal);

  // react-async doesn't support 'skip' parameter, so we need to conditionally provide the promise
  const asyncConfig = skipFetch
    ? {
        // Don't provide promiseFn when skipping to prevent any API calls
        watch: showRoomSelectModal,
      }
    : {
        promiseFn: getHotelDmcRoom,
        id: decodedHotelId,
        locale,
        filters: {
          startAt: formatDate(experience.filters?.startAt, 'YYYY-MM-DD'),
          endAt: formatDate(experience.filters?.endAt, 'YYYY-MM-DD'),
          rooms: roomsComposition.map(room => ({
            ...room,
            firstAdultNationality: room?.firstAdultNationality?.value,
          })),
        },
        watch: showRoomSelectModal,
      };

  const { data: roomsData, isPending: isLoadingRooms } = useAsync(asyncConfig);

  // Auto-select room if it matches the stored roomKey
  React.useEffect(() => {
    // For hotels, roomKey is stored in experience.data.roomKey
    const roomKey =
      experience?.type === 'hotel'
        ? experience?.data?.roomKey
        : experience?.roomKey;

    if (
      !hasAutoSelected &&
      roomsData?.data?.possibilities &&
      (!experience.selectedRooms || experience.selectedRooms.length === 0)
    ) {
      let matchedRoom = null;

      // First try to match by roomKey if it exists
      if (roomKey) {
        matchedRoom = findRoomByKey(roomKey, roomsData.data.possibilities);
      }

      // Fallback: if no roomKey or no match, select cheapest room (first in list)
      if (!matchedRoom && roomsData.data.possibilities.length > 0) {
        matchedRoom = roomsData.data.possibilities[0];
      }

      if (matchedRoom) {
        // Transform the room to have the bed property (use first bed by default)
        const roomWithBed = {
          ...matchedRoom,
          bed: matchedRoom.bedChoices?.[0] || matchedRoom.bedChoices?.[0],
        };

        // Use internalId for unique identification (multiple bookings of same hotel)
        const id = basket.indexOf(
          basket.find(elem => elem.internalId === experience.internalId),
        );

        setBasket(basket => {
          const newBasket = [...basket];
          newBasket[id].selectedRooms = [roomWithBed];
          newBasket[id].requestId = roomsData?.data?.requestId;
          // Ensure composition is stored in filters for participant calculation
          if (roomsComposition.length > 0) {
            newBasket[id].filters = {
              ...newBasket[id].filters,
              experienceComposition: roomsComposition,
            };
          }
          newBasket[id].newRoom = true; // Trigger refreshBasket in ExperienceBookingCard
          return newBasket;
        });
        setHasAutoSelected(true);
      }
    }
  }, [
    experience?.roomKey,
    experience?.data?.roomKey,
    roomsData?.data?.possibilities,
    hasAutoSelected,
    experience.selectedRooms,
  ]);

  const handleChooseRoom = () => {
    setShowRoomSelectModal(true);
  };

  const datesOverlap = (experienceFilters, hotelFilters) => {
    if (
      !hotelFilters?.startAt ||
      !hotelFilters?.endAt ||
      !experienceFilters?.startAt ||
      !experienceFilters?.endAt
    ) {
      return false;
    }

    const hotelStart = new Date(hotelFilters.startAt).getTime();
    const hotelEnd = new Date(hotelFilters.endAt).getTime();
    const expStart = new Date(experienceFilters.startAt).getTime();
    const expEnd = new Date(experienceFilters.endAt).getTime();

    // Overlap inclusif
    return hotelStart <= expEnd && hotelEnd >= expStart;
  };

  const handleDeleteRoom = () => {
    const id = basket.indexOf(basket.find(elem => elem.id === experience.id));
    setBasket(basket => {
      const newBasket = [...basket];
      newBasket[id].selectedRooms = [];
      return newBasket;
    });
    setExpirationDate(undefined);
    refreshBasket();
  };

  const handleRemoveExperience = () => {
    removeExperienceFromBasket(experience);

    // Remove hotel from Zustand store if we're in trip designer context
    const isInTripDesigner = days && days.length > 0;

    if (isInTripDesigner && experience.hotelId) {
      // Remove hotel from days
      const updatedDays = days.map(day => {
        if (day.hotelId === experience.hotelId) {
          return {
            ...day,
            hotelId: null,
            hotelPricing: null,
            hotelBookedOnMyOwn: true,
          };
        }
        return day;
      });
      updateDays(updatedDays);

      // Remove hotel from hotels map (normalized structure)
      // Note: setHotel with undefined/null doesn't remove from map, so we skip this
      // The hotel will remain in the map but won't be referenced by any day
    } else if (!isInTripDesigner && experience.hotelId) {
      const decodedExperienceHotelId = decodeId(experience.hotelId)?.toString();

      const updatedBasket = basket.map(item => {
        if (!item.selectedHotels || !decodedExperienceHotelId) {
          return item;
        }

        let hasOverlap = false;
        return {
          ...item,
          selectedHotels: item.selectedHotels.map(hotel => {
            const hotelIdDecoded = decodeId(hotel.hotelId)?.toString();
            const hotelIdMatch = hotelIdDecoded === decodedExperienceHotelId;
            hasOverlap = datesOverlap(experience.filters, hotel.data?.filters);

            if (!hotelIdMatch || !hasOverlap) {
              return { ...hotel };
            }

            // Only remove hotel if hotelId matches AND dates overlap
            if (hotelIdMatch && hasOverlap) {
              // experience.hasOverlap = true; // For debugging purposes
              return {
                ...hotel,
                data: {},
                hotelId: null,
                bookedOnMyOwn: true,
              };
            }

            return hotel;
          }),
          hasOverlap: hasOverlap,
        };
      });

      // Filter to remove the current experience from basket
      // Use internalId instead of hotelId to avoid removing other experiences with same hotel
      const newBasket = updatedBasket.filter(item => {
        const shouldRemove = item.internalId === experience.internalId;
        return !shouldRemove;
      });

      setBasket(newBasket);
    }
  };

  let includedExtras = [];
  if (days?.length > 0) {
    experience?.programs?.forEach((program, index) => {
      program.includedExperiences.forEach(extra => {
        // Calculate total adults and children across all rooms
        const totalAdults = roomsComposition.reduce(
          (sum, room) => sum + (room?.adults || 0),
          0,
        );
        const allChildrenBirthdates = roomsComposition.flatMap(
          room => room?.childrenBirthdates || [],
        );

        extra = {
          id: extra.id,
          name: extra?.name,
          adultCount: totalAdults,
          childrenBirthdates: allChildrenBirthdates,
          experienceId: experience?.id,
          bestMatches: experience?.bestMatches ?? [],
          incompatibilities: experience?.incompatibilities ?? [],
          price: undefined,
          priceWithoutPromo: undefined,
          scope: ExtraScopeEnum.DAY,
          dayIndex: index,
          isIncluded: true,
          source: ExtraSourceEnum.PROGRAM_INCLUDED,
        };
        includedExtras.push(extra);
      });
    });
  }

  const availableExtrasIds = new Set(
    experience?.price?.extras?.filter(e => e.pricings.length).map(e => e.id),
  );

  // An option the user picked is never an included service. Clear the flag
  // instead of trusting it: the basket is refreshed in place, so a stale `true`
  // written by an earlier pass would keep the option priceless and labelled
  // "included" for the rest of the session. What the synthesis owns keeps its
  // own flag — clearing that one would demote a genuine included service to a
  // priced option. It is dropped outright when `includedExtras` above rebuilds
  // it, so a stale copy cannot shadow the regenerated one.
  const carriedExtras = experience.selectedExtras.reduce((acc, e) => {
    if (isProgramIncludedExtra(e)) {
      if (!isCard) {
        acc.push(e);
      }
    } else {
      acc.push({ ...e, isIncluded: false });
    }
    return acc;
  }, []);

  const selectedAvailableExtras = [
    ...carriedExtras,
    ...(isCard ? includedExtras : []),
  ].filter(e => availableExtrasIds?.has(e.id));

  // Keep the first entry for each product. `experience.selectedExtras` comes
  // first, so an option the user picked always wins over the entry synthesized
  // from the program's included experiences: an option added by hand is never
  // an included service, and letting the synthesized one win showed it as
  // included and without a price.
  //
  // KD-3650 — occurrences are the exception: a service retained twice on a day
  // is two lines that agree on everything but their occurrence key, and each
  // carries its own travellers and its own price. Only a synthesized service,
  // which owns no occurrence, is still matched on the product alone.
  const claimedProductKeys = new Set();
  const uniqueSelectedAvailableExtras = [
    ...selectedAvailableExtras
      .reduce((acc, e) => {
        const productKey = getExtraProductKey(e);
        const isSynthesized = isProgramIncludedExtra(e);
        const key = isSynthesized
          ? productKey
          : getExtraOccurrenceIdentityKey(e);

        if (isSynthesized && claimedProductKeys.has(productKey)) {
          return acc;
        }

        if (!acc.has(key)) {
          claimedProductKeys.add(productKey);
          acc.set(key, e);
        }
        return acc;
      }, new Map())
      .values(),
  ];

  // Calculate participants from composition (works for both hotels and regular experiences)
  const participants = (() => {
    // For hotels, use roomsComposition
    if (isHotelType && roomsComposition.length > 0) {
      return roomsComposition.reduce((acc, room) => acc + getRoomPax(room), 0);
    }

    // For regular experiences, use experienceComposition from experience object or filters
    const composition =
      experience?.experienceComposition ||
      experience?.filters?.experienceComposition;

    if (composition) {
      const compositionArray = Array.isArray(composition)
        ? composition
        : [composition];
      return compositionArray.reduce(
        (acc, comp) =>
          acc + (comp?.adults || 0) + (comp?.childrenBirthdates?.length || 0),
        0,
      );
    }

    return 0;
  })();

  let experiencePriceWithPromo = experience?.price?.price;
  let experiencePriceWithoutPromo = experience?.price?.totalPriceWithoutPromo;

  if (experiencePriceWithoutPromo < 0) {
    experiencePriceWithoutPromo = 0;
    experiencePriceWithPromo = 0;
  }

  const experienceName = experience.name?.split(' ').join('-');

  const hotelPricingData = experience.hotelPricing || {};
  const hotelRoom = hotelPricingData?.rooms?.sort(
    (a, b) => a.price - b.price,
  )[0];

  const experienceInfos = (
    <>
      {isHotel &&
        experience.selectedRooms &&
        expirationDate &&
        !bookingDone && (
          <div className="absolute block right-0 top-0">
            {expirationDate && (
              <BookingCountdown
                expiryDate={expirationDate}
                resetTunnel={() => handleDeleteRoom()}
              />
            )}
          </div>
        )}

      {booking && <ExperiencesBasketState state={booking?.state} />}

      <div className="flex items-center space-x-3 justify-center">
        <div
          className={[
            'text-gray-800 font-medium',
            isCard ? 'text-xl text-center' : 'text-2xl text-center',
          ].join(' ')}
        >
          {experience.name}
        </div>

        {[
          ExperiencePricingTypeEnum.SIC,
          ExperiencePricingTypeEnum.SERIES,
        ].includes(experience?.pricingType) && (
          <div>
            <ExperienceGuaranteedDepartureIcon absolute={false} />
          </div>
        )}
      </div>

      <div
        className={[
          'text-gray-500 w-full',
          isCard ? 'text-base text-center' : 'text-xl text-center',
        ].join(' ')}
      >
        {[
          experience?.inCity?.title ?? experience?.city?.title,
          experience?.inCity?.country?.title ?? experience?.country?.title,
        ]
          .filter(Boolean)
          .join(', ')}
      </div>

      <div className="mt-3 text-xl text-center text-gray-800 w-full">
        <div className="flex justify-center items-center space-x-3">
          <div>{formatDate(experience.filters.startAt, 'DD/MM/YYYY')}</div>

          <i className="far fa-arrow-right text-gray-400" />

          <div>{formatDate(experience.filters.endAt, 'DD/MM/YYYY')}</div>
        </div>
      </div>

      {!isCard &&
        !isCheckout &&
        ExperienceType.isProgram(experience?.experienceType) && (
          <ExperienceBasketProgramContent
            experienceId={experience?.id}
            selectedHotels={experience?.selectedHotels}
            tripDays={days}
            experiencesMap={experiences}
            hotelsMap={hotels}
          />
        )}

      <div className="mt-3 text-gray-500 flex justify-between items-center">
        <div className="flex space-x-1.5 items-center">
          <i className="far fa-users" />

          <div>
            {t('participants', {
              count: participants,
            })}
          </div>
        </div>

        <div className="text-right">
          {showPrice && experience?.price?.price !== undefined ? (
            <div>
              {experience?.price.price !==
                experience?.price?.totalPriceWithoutPromo && (
                <div className="text-sm font-medium line-through">
                  {formatCurrency({
                    amount: experiencePriceWithoutPromo,
                    currency: experience?.price.currency,
                  })}
                </div>
              )}

              {experience?.price?.currency && (
                <>
                  <div
                    className="text-lg text-orange-600 font-semibold"
                    data-testid={`experience-${experienceName}`}
                  >
                    {formatCurrency({
                      amount: experiencePriceWithPromo,
                      currency: experience?.price?.currency,
                    })}
                  </div>

                  <div className="text-sm text-gray-600 font-medium">
                    {formatCurrency({
                      amount: experiencePriceWithPromo / participants,
                      currency: experience?.price?.currency,
                    })}{' '}
                    / {t('pax')}
                  </div>
                </>
              )}
            </div>
          ) : (
            experience.type === 'hotel' &&
            (hotelPricingData?.price ??
              hotelRoom?.price ??
              experience?.price) && (
              <div className="text-right">
                <div className="text-lg text-orange-600 font-semibold">
                  {formatCurrency({
                    amount:
                      hotelPricingData?.price ??
                      hotelRoom?.price ??
                      experience?.price,
                    currency:
                      hotelPricingData?.currency ??
                      experience?.connectionRequestCurrency,
                  })}
                </div>

                {participants > 0 && (
                  <div className="text-sm text-gray-600 font-medium">
                    {formatCurrency({
                      amount:
                        (hotelPricingData?.price ??
                          hotelRoom?.price ??
                          experience?.price) / participants,
                      currency:
                        hotelPricingData?.currency ??
                        experience?.connectionRequestCurrency,
                    })}{' '}
                    / {t('pax')}
                  </div>
                )}

                {hotelRoom?.displayName && (
                  <div className="text-blue-500 content-center mt-1">
                    <div className="text-xs">{hotelRoom?.displayName}</div>
                  </div>
                )}
              </div>
            )
          )}

          {!experience.price && (
            <div className="text-lg font-semibold">{t('notAvailable')}</div>
          )}
        </div>
      </div>

      {/* Display room name and price for hotels */}
      {(() => {
        const shouldShowRoom =
          !isCheckout &&
          experience?.type === 'hotel' &&
          experience?.data?.roomName &&
          experience?.data?.price;

        return shouldShowRoom ? (
          <div className="mt-3 bg-gray-50 rounded-lg p-3">
            <div className="flex justify-between items-center">
              <div className="text-sm font-medium text-gray-700">
                {experience.data.roomName}
              </div>
              <div className="text-sm font-semibold text-orange-600">
                {formatCurrency({
                  amount: experience.data.price,
                  currency:
                    experience.data.currency || experience?.price?.currency,
                })}
              </div>
            </div>
          </div>
        ) : null;
      })()}

      {isCard && experience?.type !== 'hotel' && (
        <div className="my-2">
          <LigneHorizontal />

          <KoobAccordion title={t('optionsLabel')}>
            <ExperienceBasketExtras
              experience={experience}
              selectedExtras={uniqueSelectedAvailableExtras}
              currency={experience?.price?.currency}
              bookingDone={false}
              locale={locale}
            />
          </KoobAccordion>
        </div>
      )}

      {experience.price?.promotions && (
        <Stack
          style={{ flexWrap: 'wrap', marginTop: '5px', marginBottom: '5px' }}
        >
          {(experience.price?.promotions || []).map((promo, index) => (
            <Promotion key={'promo_' + index} style={{ marginRight: '5px' }}>
              {promo}
            </Promotion>
          ))}
        </Stack>
      )}

      {experience.bookingId && (
        <div className="mt-3">
          <KoobButton
            variant="primary"
            onClick={() => {
              setNewRoom(true);
              navigate(`/bookings/${experience.bookingId}`);
            }}
          >
            {t('goToBooking')}
          </KoobButton>
        </div>
      )}
    </>
  );

  return (
    <div
      className={`relative flex-none mb-3 mx-1 p-4 ${
        isCard ? 'border rounded-lg overflow-hidden' : ''
      }`}
    >
      {enableRemoval && (
        <div className="absolute top-5 right-5">
          <button
            onClick={handleRemoveExperience}
            className="text-gray-500/75 hover:text-gray-500 outline-none border-0"
          >
            <svg className="h-5 w-5" viewBox="0 0 512 512">
              <path d="M256 48a208 208 0 1 1 0 416 208 208 0 1 1 0-416zm0 464A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM175 175c-9.4 9.4-9.4 24.6 0 33.9l47 47-47 47c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0l47-47 47 47c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-47-47 47-47c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-47 47-47-47c-9.4-9.4-24.6-9.4-33.9 0z" />
            </svg>
          </button>
        </div>
      )}

      {experience.state && (
        <div className="mb-5">
          <BookingState state={experience?.state} type={t('experience')} />
        </div>
      )}

      {!isCard && experience?.type === 'hotel' && (
        <>
          {experienceInfos}
          {(!experience.selectedRooms ||
            experience?.selectedRooms?.length <= 0) && (
            <div className="flex justify-center items-center">
              {isLoadingRooms && !showRoomSelectModal ? (
                <div className="py-4">
                  <KoobLoader size={24} />
                </div>
              ) : (
                <div className="w-80">
                  <KoobButton
                    variant="primary"
                    tabIndex="2"
                    block
                    onClick={handleChooseRoom}
                    testId="choose-room"
                    disabled={isLoadingRooms}
                  >
                    {t('chooseRoom')}
                  </KoobButton>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {(experience?.type !== 'hotel' || isCard) && experienceInfos}

      {/* Room Selection Modal */}
      {!isCard &&
        experience?.type === 'hotel' &&
        roomsData?.data?.possibilities && (
          <HotelRoomSelectionModal
            isOpen={showRoomSelectModal}
            onRequestClose={() => {
              setShowRoomSelectModal(false);
            }}
            filters={{
              rooms: roomsComposition,
              // The stay the modal states extra room-nights against (KD-3496).
              startAt: formatDate(experience.filters?.startAt, 'YYYY-MM-DD'),
              endAt: formatDate(experience.filters?.endAt, 'YYYY-MM-DD'),
            }}
            onSave={rooms => {
              // Update basket with selected rooms
              if (rooms.length > 0) {
                const basketIndex = basket.indexOf(
                  basket.find(elem => elem.id === experience.id),
                );

                // Extract existing travelers from FORM values (not basket) to preserve user-entered data
                const existingTravelers = [];
                if (formik && formik.values.experiencesToBook) {
                  // Find the correct form index - form filters basket by .price so indices don't match
                  const formIndex = formik.values.experiencesToBook.findIndex(
                    exp => exp.self?.id === experience.id,
                  );

                  if (formIndex >= 0) {
                    const formExperience =
                      formik.values.experiencesToBook[formIndex];

                    if (formExperience.selectedRooms) {
                      formExperience.selectedRooms.forEach(room => {
                        if (room.travelers) {
                          existingTravelers.push(...room.travelers);
                        }
                      });
                    }
                  }
                }

                // If no form values, fall back to basket
                if (
                  existingTravelers.length === 0 &&
                  basket[basketIndex].selectedRooms
                ) {
                  basket[basketIndex].selectedRooms.forEach(room => {
                    if (room.travelers) {
                      existingTravelers.push(...room.travelers);
                    }
                  });
                }

                setBasket(basket => {
                  const newBasket = [...basket];

                  // Redistribute existing travelers to new rooms immediately
                  if (existingTravelers.length > 0) {
                    const travelersByRoom = assignTravelersToRooms(
                      existingTravelers,
                      roomsComposition,
                      rooms.length,
                    );
                    const roomsWithTravelers = rooms.map((room, roomIdx) => ({
                      ...room,
                      travelers: travelersByRoom[roomIdx],
                    }));
                    newBasket[basketIndex].selectedRooms = roomsWithTravelers;
                    newBasket[basketIndex].travelers = existingTravelers; // Store at experience level too
                  } else {
                    newBasket[basketIndex].selectedRooms = rooms;
                  }

                  newBasket[basketIndex].requestId = roomsData?.data?.requestId;
                  // Ensure composition is stored in filters for participant calculation
                  if (roomsComposition.length > 0) {
                    newBasket[basketIndex].filters = {
                      ...newBasket[basketIndex].filters,
                      experienceComposition: roomsComposition,
                    };
                  }
                  return newBasket;
                });

                // Also update Formik values directly to prevent reinitialization from overwriting
                if (formik && formik.values.experiencesToBook) {
                  const formIndex = formik.values.experiencesToBook.findIndex(
                    exp => exp.self?.id === experience.id,
                  );

                  if (formIndex >= 0) {
                    // Always update requestId
                    formik.setFieldValue(
                      `experiencesToBook.${formIndex}.requestId`,
                      roomsData?.data?.requestId,
                      false, // Don't validate
                    );

                    if (existingTravelers.length > 0) {
                      // Redistribute travelers in form values
                      const formTravelersByRoom = assignTravelersToRooms(
                        existingTravelers,
                        roomsComposition,
                        rooms.length,
                      );
                      const formRoomsWithTravelers = rooms.map(
                        (room, roomIdx) => ({
                          ...room,
                          travelers: formTravelersByRoom[roomIdx],
                        }),
                      );

                      formik.setFieldValue(
                        `experiencesToBook.${formIndex}.selectedRooms`,
                        formRoomsWithTravelers,
                        false, // Don't validate
                      );
                    }
                  }
                }

                setShowRoomSelectModal(false);
                // Don't call refreshBasket here - we've already handled traveler
                // redistribution in the save handler above. Calling refreshBasket
                // would regenerate travelers and lose user-entered form data.
              }
            }}
            hotelData={{
              possibilities: roomsData?.data?.possibilities || [],
            }}
            totalRoomNumber={roomsComposition.length}
            hotelName={experience.name}
            initialSelectedRooms={(() => {
              // Try to auto-select room(s) based on stored roomKey(s)
              // For hotels, roomKey is stored in experience.data.roomKey
              // Support comma-separated keys for multiple rooms
              const roomKey =
                experience?.type === 'hotel'
                  ? experience?.data?.roomKey
                  : experience?.roomKey;

              if (roomKey && roomsData?.data?.possibilities) {
                // Check if roomKey contains commas (multiple rooms)
                if (roomKey.includes(',')) {
                  return findRoomsByKeys(roomKey, roomsData.data.possibilities);
                } else {
                  const matchedRoom = findRoomByKey(
                    roomKey,
                    roomsData.data.possibilities,
                  );
                  return matchedRoom ? [matchedRoom] : [];
                }
              }
              return [];
            })()}
          />
        )}
    </div>
  );
};

export default function ExperiencesBasket({ open, setOpen }) {
  const { basket } = useExperienceBooking();
  const { t } = useTranslation('experiences', { keyPrefix: 'basket' });
  const navigate = useNavigate();

  return (
    <KoobSlideOver
      open={open}
      setOpen={setOpen}
      title={t('title')}
      widthClass="max-w-[700px]"
      footer={
        basket.length !== 0 && (
          <KoobButton
            variant="primary"
            onClick={() => {
              navigate('/experiences/checkout');
              setOpen(false);
            }}
            testId="checkout-basket"
          >
            {t('checkout')}
          </KoobButton>
        )
      }
    >
      {basket.length === 0 && <div>{t('empty')}</div>}

      {[...basket]
        .sort(
          (a, b) => new Date(a.filters?.startAt) - new Date(b.filters?.startAt),
        )
        .map(experience => (
          <ExperienceBasketCard
            experience={experience}
            key={experience.internalId}
          />
        ))}
    </KoobSlideOver>
  );
}

export const SecondaryOptions = ({ selectedExtras, priceObj, currency }) => {
  return (
    <>
      <div className="bg-gray-100 rounded-lg p-4 max-w-1/2">
        <div className="flex flex-col space-y-3">
          {selectedExtras.length > 0
            ? selectedExtras.map((extra, key) => {
                let extraTitle = null;
                const pax = extra.perPax
                  ? (extra.adultCount + extra.childrenBirthdates?.length ?? 0)
                  : 1;
                const price = extra.price * pax;
                const priceWithoutPromo = extra.priceWithoutPromo * pax;

                return (
                  <>
                    {extraTitle}

                    <div key={key} className={'flex flex-col space-y-1'}>
                      <div className="flex space-x-3 items-center">
                        <div
                          className="font-medium text-gray-500 text-sm"
                          data-testid={`secondary-option-day-${
                            extra.dayIndex + 1
                          }-${extra.name?.split(' ').join('-')}`}
                        >
                          {extra.name}
                        </div>

                        <ExperienceExtraComposition
                          adults={extra.adultCount ?? 0}
                          childrenCount={extra.childrenBirthdates?.length ?? 0}
                        />

                        <div className="font-medium text-sm">
                          <div className="flex space-x-2 justify-end text-orange-600">
                            <div
                              data-testid={`secondary-option-${
                                extra.dayIndex + 1
                              }-${extra.name?.split(' ').join('-')}`}
                            >
                              {formatCurrency({
                                amount: price,
                                currency: extra?.currency ?? currency,
                              })}
                            </div>

                            {extra?.price !== extra?.priceWithoutPromo && (
                              <div className="text-xs line-through text-gray-500">
                                {formatCurrency({
                                  amount: priceWithoutPromo,
                                  currency: priceObj?.currency ?? currency,
                                })}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="flex-end">
                          <KoobBadge
                            color={
                              extra?.type === 'required' ? 'orange' : 'gray'
                            }
                          >
                            {extra?.type}
                          </KoobBadge>
                        </div>
                      </div>
                    </div>
                  </>
                );
              })
            : null}
        </div>
      </div>
    </>
  );
};

export const ExperienceBasketExtras = ({
  selectedExtras,
  setSelectedExtras,
  experience,
  priceObj,
  currency,
  bookingDone,
  locale,
}) => {
  const { t } = useTranslation('experiences');
  const { experienceFilters } = useExperienceBooking();

  const [showContractingCondition, setShowContractingCondition] =
    useState(false);
  const [extraId, setExtraId] = useState();
  const [showExtrasModal, setShowExtrasModal] = useState(false);

  const allowChanges = !!setSelectedExtras;

  const sortedExtras = sortExtras(selectedExtras);

  const extrasGroup = new Set();

  const { data } = useAsync({
    promiseFn: getExperience,
    experienceId: experience?.id,
    locale,
  });

  // Only the entries synthesized from the program's `includedExperiences`
  // carry `isIncluded`. An option added by hand is never one of them, whatever
  // its price — keying this off a missing price hid free options from the
  // "add an option" list.
  const includedExtras = sortedExtras?.reduce((acc, extra) => {
    if (extra.isIncluded) {
      acc.push(extra.id);
    }
    return acc;
  }, []);

  const experienceExtras = (data?.data?.extras ?? []).filter(
    extra => !includedExtras.includes(extra.id),
  );

  return (
    <>
      <div className="bg-gray-100 rounded-lg p-4 max-w-1/2">
        <div className="flex flex-col space-y-3">
          {sortedExtras.length > 0 ? (
            sortedExtras
              ?.filter(se => !se.parentExtraId)
              ?.map((extra, key) => {
                const extraTitle = getExtraTitle({
                  extra,
                  extrasGroup,
                  translation: t,
                });
                // KD-3650 — a sub-option belongs to the occurrence it was
                // ticked under: matching on the parent service alone counted
                // it, and its price, under every occurrence of that service.
                const extraChildren = getSecondaryExtras(sortedExtras, extra);
                const pax = extra.perPax
                  ? extra?.adultCount + extra.childrenBirthdates?.length
                  : 1;
                let totalExtraPrice = (extra?.price ?? 0) * pax;
                let totalExtraPriceWithoutPromo =
                  extra?.priceWithoutPromo * pax ?? 0;
                extraChildren.forEach(extraChild => {
                  totalExtraPrice +=
                    extraChild?.price *
                      (extraChild.perPax
                        ? extraChild.adultCount +
                          extraChild.childrenBirthdates?.length
                        : 1) ?? 0;
                  totalExtraPriceWithoutPromo +=
                    extraChild?.priceWithoutPromo *
                      (extraChild.perPax
                        ? extraChild.adultCount +
                          extraChild.childrenBirthdates?.length
                        : 1) ?? 0;
                });

                const secondaryExtras = getSecondaryExtras(
                  selectedExtras,
                  extra,
                );
                const experienceName = extra.name?.split(' ').join('-');

                return (
                  <React.Fragment key={key}>
                    {extraTitle}

                    <div
                      className={
                        allowChanges
                          ? 'flex justify-between items-center'
                          : 'flex flex-col space-y-1'
                      }
                      data-testid={`day-${extra.dayIndex + 1}`}
                    >
                      <div className="flex space-x-3 items-center">
                        <div
                          className="font-medium text-gray-500 text-sm"
                          data-testid={`primary-option-day-${
                            extra.dayIndex + 1
                          }-${extra.name?.split(' ').join('-')}`}
                        >
                          {extra.name}
                        </div>

                        <ExperienceExtraComposition
                          adults={extra.adultCount ?? 0}
                          childrenCount={extra.childrenBirthdates?.length ?? 0}
                        />
                      </div>

                      <div className="font-medium text-sm">
                        {/* "Included" states what the extra is, not that it
                            costs nothing: an option picked in the trip
                            designer is never an included service, even when
                            the DMC prices it at zero. */}
                        {!extra?.isIncluded ? (
                          <div className="flex space-x-2 justify-end text-orange-600">
                            <div data-testid={`option-${experienceName}`}>
                              {formatCurrency({
                                amount: totalExtraPrice,
                                currency: extra?.currency ?? currency,
                              })}
                            </div>

                            {extra?.price !== extra?.priceWithoutPromo && (
                              <div className="text-xs line-through text-gray-500">
                                {formatCurrency({
                                  amount: totalExtraPriceWithoutPromo,
                                  currency: priceObj?.currency ?? currency,
                                })}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div>{`${t('extras.included')}`}</div>
                        )}
                      </div>
                    </div>

                    {secondaryExtras.length > 0 && (
                      <KoobAccordion
                        title={t('optionsLabel')}
                        data-testid={`${extra.name
                          ?.split(' ')
                          .join('-')}-secondary-options`}
                      >
                        <SecondaryOptions
                          selectedExtras={secondaryExtras}
                          priceObj={priceObj}
                          bookingDone={bookingDone}
                        />
                      </KoobAccordion>
                    )}

                    {/* Same rule as the amount above: an option quoted at 0
                        still states its unit price. Only an included service,
                        which carries no amount at all, skips this line. */}
                    {!extra?.isIncluded && (
                      <div className="flex space-x-2 justify-end">
                        <div className="flex space-x-2 justify-end">
                          <div>
                            {formatCurrency({
                              amount: totalExtraPrice / pax,
                              currency: extra?.currency ?? currency,
                            })}{' '}
                            / PAX
                          </div>

                          {extra?.price !== extra?.priceWithoutPromo && (
                            <div className="text-xs line-through text-gray-500">
                              {formatCurrency({
                                amount: totalExtraPriceWithoutPromo / pax,
                                currency: priceObj?.currency ?? currency,
                              })}{' '}
                              / PAX
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {extra.cancellationPolicies && (
                      <div className="font-medium text-gray-500 text-sm">
                        {t('basket.agreementTextOne')}
                        <ModalCondition
                          onClick={() => {
                            setExtraId(key);
                            setShowContractingCondition(true);
                          }}
                        >
                          {t('basket.conditions')}
                        </ModalCondition>

                        {t('basket.agreementTextTwoOption')}
                      </div>
                    )}
                  </React.Fragment>
                );
              })
          ) : (
            <div className="font-medium text-gray-500 text-sm">
              {t('extras.noExtras')}
            </div>
          )}
        </div>
      </div>

      {showContractingCondition && (
        <KoobModal
          open={true}
          setOpen={() => setShowContractingCondition(false)}
          widthClass="sm:max-w-5xl"
        >
          <ExperienceDynamicCancelConditions
            cancelConditions={
              selectedExtras[extraId]?.cancelConditions ??
              selectedExtras[extraId]?.policies?.cancelConditions ??
              []
            }
            currency={selectedExtras[extraId]?.currency ?? priceObj?.currency}
            translationKey="booking"
          />

          <div className="max-h-[600px] overflow-y-auto">
            <div className="px-3">
              <KoobTitle>{t('basket.contractingPolicies')}</KoobTitle>
            </div>
            <div className="mt-4">
              <div
                dangerouslySetInnerHTML={{
                  __html: sanitizeRichText(
                    selectedExtras[extraId].cancellationPolicies,
                  ),
                }}
              />
            </div>
          </div>

          <div className="mt-5 flex justify-end">
            <KoobButton
              variant="primary"
              onClick={() => {
                setShowContractingCondition(false);
              }}
            >
              {t('misc:close')}
            </KoobButton>
          </div>
        </KoobModal>
      )}

      {allowChanges && (
        <ExperienceExtrasModal
          open={showExtrasModal}
          setOpen={setShowExtrasModal}
          experience={data?.data}
          experienceComposition={experienceFilters?.experienceComposition?.[0]}
          extras={experienceExtras}
          prices={getResolvedExtrasPrices(priceObj)}
          currency={priceObj?.currency}
          selectedExtras={selectedExtras.filter(
            extra => !includedExtras?.includes(extra.id),
          )}
          setSelectedExtras={setSelectedExtras}
          onContinue={() => setShowExtrasModal(false)}
        />
      )}
    </>
  );
};
